import React, { useState } from 'react';
import axios from 'axios';
import './SubmissionForm.css';

// --- Hilfs-Komponente für sichere E-Mail-Darstellung ---
const SafeMailLink = () => {
  const user = "beschaffung";
  const domain = "hs-bochum.de";
  return (
    <a href={`mailto:${user}@${domain}`}>
      {user}@{domain}
    </a>
  );
};
// -------------------------------------------------------

function SubmissionForm() {
  const [formData, setFormData] = useState({
    name: '',
    kostenstelle: '',
    serviceName: '',
    gesamterstattungsbetrag: '',
    additionalInfo: '',
    checkbox1: false,
    checkbox2: false
  });

  // ÄNDERUNG 1: State ist jetzt ein Array (Liste) statt null
  const [pdfFiles, setPdfFiles] = useState([]); 
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'kostenstelle') {
      if (!/^\d*$/.test(value)) return; 
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // ÄNDERUNG 2: Logik für mehrere Dateien
  const handleFileChange = (e) => {
    // Umwandlung von FileList in ein echtes Array
    const files = Array.from(e.target.files); 
    validateAndAddFiles(files);
    // Input zurücksetzen, damit man dieselbe Datei nochmal wählen könnte wenn man wollte
    e.target.value = null; 
  };

  const validateAndAddFiles = (newFiles) => {
    const validFiles = [];
    let errorMsg = '';

    newFiles.forEach(file => {
      if (file.type !== 'application/pdf') {
        errorMsg = 'Bitte laden Sie nur PDF-Dateien hoch.';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        errorMsg = `Die Datei "${file.name}" ist zu groß (Max 10MB).`;
        return;
      }
      // Optional: Prüfen ob Datei schon existiert (anhand Name und Größe)
      const alreadyExists = pdfFiles.some(existing => 
        existing.name === file.name && existing.size === file.size
      );
      
      if (!alreadyExists) {
        validFiles.push(file);
      }
    });

    if (errorMsg && validFiles.length === 0) {
      setMessage({ type: 'error', text: errorMsg });
    } else {
      setMessage({ type: '', text: '' });
      // Neue Dateien zu den bestehenden hinzufügen
      setPdfFiles(prevFiles => [...prevFiles, ...validFiles]);
    }
  };

  // ÄNDERUNG 3: Datei entfernen Funktion
  const removeFile = (indexToRemove) => {
    setPdfFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    validateAndAddFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.name || !formData.kostenstelle || !formData.serviceName || !formData.gesamterstattungsbetrag) {
      setMessage({ type: 'error', text: 'Bitte füllen Sie alle Pflichtfelder aus.' });
      return;
    }
    if (!formData.checkbox1 || !formData.checkbox2) {
      setMessage({ type: 'error', text: 'Bitte bestätigen Sie beide Checkboxen.' });
      return;
    }
    
    // ÄNDERUNG 4: Prüfung auf Länge des Arrays
    if (pdfFiles.length === 0) {
      setMessage({ type: 'error', text: 'Bitte laden Sie mindestens eine Rechnung als PDF hoch.' });
      return;
    }

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('kostenstelle', formData.kostenstelle);
    submitData.append('serviceName', formData.serviceName);
    submitData.append('gesamterstattungsbetrag', formData.gesamterstattungsbetrag);
    submitData.append('additionalInfo', formData.additionalInfo);
    submitData.append('checkbox1', formData.checkbox1);
    submitData.append('checkbox2', formData.checkbox2);

    // ÄNDERUNG 5: Alle Dateien anhängen
    pdfFiles.forEach((file) => {
      // Manche Backends erwarten hier 'pdf[]', andere kommen mit 'pdf' klar.
      // 'pdf' (mehrfach) ist Standard für FormData.
      submitData.append('pdf', file);
    });

    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/submissions', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setFormData({
          name: '',
          kostenstelle: '',
          serviceName: '',
          gesamterstattungsbetrag: '',
          additionalInfo: '',
          checkbox1: false,
          checkbox2: false
        });
        setPdfFiles([]); // Liste leeren
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Es ist ein Fehler aufgetreten.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.name &&
           formData.kostenstelle &&
           formData.serviceName &&
           formData.gesamterstattungsbetrag &&
           formData.checkbox1 &&
           formData.checkbox2 &&
           pdfFiles.length > 0; // Check ob Liste nicht leer ist
  };

  return (
    <div className="submission-form-container">
      <div className="form-card">
        <h2>Hier Erstattungsantrag einreichen</h2>
        <div className="form-hints">
          <strong>Wichtige Hinweise:</strong>
          <ul>
            <li>Es werden ausschließlich Erstattungen aus der folgenden Liste durchgeführt: <a href="https://hs-bochum.sciebo.de/s/mqqPRtzaLpQxLL2/download" target="_blank" rel="noopener noreferrer">Liste</a>.</li>
            <li>Pro Erstattungsantrag können maximal 500€ erstattet werden.</li>
            <li>Es werden ausschließlich Kosten aus dem aktuellen Quartal erstattet.</li>
            <li>Bitte füllen Sie alle Felder des Antrags aus und laden Sie die Rechnungen/Zahlungsnachweise als PDF hoch.</li>
            <li>Gehen Sie sicher, dass Sie eine ausreichend belastbare Kostenstelle haben und Ihre Anschaffung mit dieser abgesprochen ist.</li>
          </ul>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="submission-form">
          {/* Name */}
          <div className="form-group">
            <label htmlFor="name">Name <span className="required">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Ihr vollständiger Name"
              required
            />
          </div>

          {/* Kostenstelle */}
          <div className="form-group">
            <label htmlFor="kostenstelle">Kostenstelle <span className="required">*</span></label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              id="kostenstelle"
              name="kostenstelle"
              value={formData.kostenstelle}
              onChange={handleInputChange}
              placeholder="z.B. 12345"
              required
            />
          </div>

          {/* Service-Name */}
          <div className="form-group">
            <label htmlFor="serviceName">Name der Dienstleistung/des gekauften Services <span className="required">*</span></label>
            <input
              type="text"
              id="serviceName"
              name="serviceName"
              value={formData.serviceName}
              onChange={handleInputChange}
              placeholder="Beschreibung der Dienstleistung"
              required
            />
          </div>

          {/* Gesamterstattungsbetrag */}
          <div className="form-group">
            <label htmlFor="gesamterstattungsbetrag">Gesamterstattungsbetrag <span className="required">*</span></label>
            <input
              type="text"
              id="gesamterstattungsbetrag"
              name="gesamterstattungsbetrag"
              value={formData.gesamterstattungsbetrag}
              onChange={handleInputChange}
              placeholder="z.B. 49,99 €"
              required
            />
          </div>

          {/* Zusätzliche Informationen */}
          <div className="form-group">
            <label htmlFor="additionalInfo">Weitere Informationen</label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              placeholder="Optionale Anmerkungen oder zusätzliche Informationen"
              rows={4}
            />
          </div>

          {/* Checkbox 1 */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="checkbox1"
                checked={formData.checkbox1}
                onChange={handleInputChange}
                required
              />
              <span>
                Hiermit bestätige ich, dass ich für die angegebene Kostenstelle zeichnungsberechtigt bin <b>und dass die Kostenstelle ausreichend belastbar ist.</b> <span className="required">*</span>
              </span>
            </label>
          </div>

          {/* Checkbox 2 */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="checkbox2"
                checked={formData.checkbox2}
                onChange={handleInputChange}
                required
              />
              <span>
                Hiermit bestätige ich, dass der Verwaltung meine Bankdaten bekannt sind oder dass ich diese über <SafeMailLink /> der Verwaltung habe zukommen lassen. <span className="required">*</span>
              </span>
            </label>
          </div>

          {/* PDF Upload Bereich (Mehrfach) */}
          <div className="form-group">
            <label>
              Rechnungen hochladen (PDF) <span className="required">*</span>
            </label>
            <div
              className={`file-upload-area ${isDragging ? 'dragging' : ''} ${pdfFiles.length > 0 ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* ÄNDERUNG 6: Attribut 'multiple' hinzugefügt */}
              <input
                type="file"
                id="pdf-upload"
                accept="application/pdf"
                multiple 
                onChange={handleFileChange}
                className="file-input"
              />
              
              <label htmlFor="pdf-upload" className="file-upload-label">
                {/* Wenn Dateien da sind: Liste anzeigen */}
                {pdfFiles.length > 0 ? (
                  <div className="file-list">
                    <div className="file-list-header">
                      {pdfFiles.length} Datei{pdfFiles.length !== 1 && 'en'} ausgewählt:
                    </div>
                    {pdfFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="file-item">
                         <div className="file-info-row">
                            <svg className="file-icon-small" fill="currentColor" viewBox="0 0 20 20" width="16" height="16">
                              <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                            </svg>
                            <span className="file-name-small">{file.name}</span>
                            <span className="file-size-small">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                         </div>
                         {/* Löschen Button */}
                         <button 
                           type="button" 
                           className="remove-file-btn"
                           onClick={(e) => {
                             e.preventDefault(); // Verhindert Öffnen des File-Dialogs
                             removeFile(index);
                           }}
                         >
                           ✕
                         </button>
                      </div>
                    ))}
                    <div className="add-more-hint">
                      Klicken oder Ziehen um weitere hinzuzufügen
                    </div>
                  </div>
                ) : (
                  // Wenn keine Dateien da sind: Standard Anzeige
                  <div className="upload-prompt">
                    <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="upload-text">
                      PDFs hier ablegen oder <strong>klicken zum Durchsuchen</strong>
                    </span>
                    <span className="upload-hint">Mehrere Dateien möglich (Max 10MB pro Datei)</span>
                  </div>
                )}
              </label>
            </div>
          </div>
                 <span>
                Bitte beachten Sie, dass aus der Rechnung zu erkennen sein muss, dass der Betrag bereits gezahlt wurde. Falls dies aus der Rechung nicht direkt 
                ersichtlich ist, wird außerdem ein Zahlungsnachweis benötigt.
              </span>
          <div className="form-actions">
            <button
              type="submit"
              className="submit-button"
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? 'Wird gesendet...' : 'Antrag absenden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SubmissionForm;
